import { Injectable, inject } from "@angular/core";
import { BaseNotifier } from "../../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../../shared/providers/ref";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import * as intersectionOperator from "@arcgis/core/geometry/operators/intersectionOperator";
import { NowcastingKpiState } from "./kpi-state";
import { NowcastingKpiService, territorialKey } from "./kpi-service";
import { NowcastingProvider } from "../nowcasting-provider";
import { WarningProvider } from "../../warning/warning-provider";
import { KpiChartService } from "./kpi-chart-service";
import { MapProvider } from "../../map/map-provider";
import { UbigeoProvider } from "../../../shared/layer/ubigeo/ubigeo-provider";
import { KPI_HIGHLIGHT_LAYER_ID, KPI_EXPOSED_LAYER_ID } from "../../../shared/map-highlight-layers";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Color from "@arcgis/core/Color";
import Extent from "@arcgis/core/geometry/Extent";

const EMPTY_CHARTS: NowcastingKpiState.Charts = {
  treemapOptions: { chart: { type: "treemap" }, series: [] },
  heatmapOptions: { chart: { type: "heatmap" }, series: [] },
  sankeyOptions: { chart: { type: "sankey" }, series: [] },
  radarOptions: { chart: { type: "area" }, series: [] },
  barOptions: { chart: { type: "bar" }, series: [] },
  overviewStats: null,
  summaryDonutOptions: { chart: { type: "pie" }, series: [] },
  summaryBarOptions: { chart: { type: "bar" }, series: [] },
};

@Injectable()
export class NowcastingKpiNotifier extends BaseNotifier<NowcastingKpiState> {
  private readonly kpiService = inject(NowcastingKpiService);
  private readonly nowcastingProvider = inject(NowcastingProvider);
  private readonly warningProvider = inject(WarningProvider);
  private readonly chartService = inject(KpiChartService);
  private readonly mapProvider = inject(MapProvider);
  private _lastVisible = new Set<string>();
  private _lastWarningVisible = new Set<string>();

  /** Epoch SENAMHI visto (nowcasting): cambia en cada re-emisión silenciosa. */
  private _lastNcEpoch = 0;

  /** Epoch SENAMHI visto (avisos): cambia en cada re-emisión silenciosa. */
  private _lastWrnEpoch = 0;

  /** Rings acumulados por layerId (todos los niveles combinados). */
  private _ringsByLayer = new Map<number, Map<number, number[][][]>>();

  /**
   * Geometría ArcGIS pre-serializada (dissolve unificado) por nivel de alerta.
   * Se re-usa en requestLayerFeatures para el drill-down sin re-ejecutar unionRings.
   */
  private _geometryByLevel = new Map<number, string>();

  /**
   * Token monotónico de la última consulta de detalle: descarta
   * respuestas tardías de una capa cuando el usuario ya pidió otra
   * (o cuando el estado se recargó entre medias).
   */
  private _detailRequest = 0;

  constructor(ref: Ref) {
    super(ref, {
      items: [],
      loading: false,
      activeTab: "",
      charts: EMPTY_CHARTS,
      expandedLayerKey: null,
      layerDetails: new Map(),
      computedAt: null,
      scope: null,
    });
  }

  override build(): void {
    this.listen(() => {
      const nc = this.nowcastingProvider.state();
      const visible = nc.visibleItems;

      const added = new Set<string>();
      for (const key of visible) {
        if (!this._lastVisible.has(key)) added.add(key);
      }
      this._lastVisible = new Set(visible);

      // Re-emisión silenciosa SENAMHI (auto-refresh R1): la clave visible
      // es la misma pero los anillos cambiaron → recargar el cruce igual.
      const ncEpoch = nc.refreshEpoch;
      const reEmitted = ncEpoch !== this._lastNcEpoch;
      this._lastNcEpoch = ncEpoch;

      if (added.size > 0) {
        const [firstKey] = added;
        void this.loadFromLayer(nc.activeTab, firstKey);
      } else if (reEmitted && visible.size > 0) {
        const [firstKey] = visible;
        void this.loadFromLayer(nc.activeTab, firstKey);
      }
    });

    // Avisos (warnings): misma alimentación del KPI que nowcasting.
    this.listen(() => {
      const w = this.warningProvider.state();
      const visible = w.visibleItems;

      const added = new Set<string>();
      for (const key of visible) {
        if (!this._lastWarningVisible.has(key)) added.add(key);
      }
      this._lastWarningVisible = new Set(visible);

      const wrnEpoch = w.refreshEpoch;
      const reEmitted = wrnEpoch !== this._lastWrnEpoch;
      this._lastWrnEpoch = wrnEpoch;

      if (added.size > 0) {
        const [firstKey] = added;
        void this.loadFromLayer("avisos", firstKey);
      } else if (reEmitted && visible.size > 0) {
        const [firstKey] = visible;
        void this.loadFromLayer("avisos", firstKey);
      }
    });

    // Ámbito del cruce: al cambiar el ubigeo del header (código o llegada
    // de la geometría, que es async) se limpia el resaltado previo del KPI
    // y se RELANZA el cruce de la ALERTA ACTIVA recortado al polígono
    // seleccionado (departamento/provincia/distrito). Nacional = sin recorte.
    this.listen(() => {
      const ub = this.ref.watch(UbigeoProvider).state();
      const code =
        ub.selectedDistrict || ub.selectedProvince || ub.selectedDepartment || null;
      const geometry = ub.currentGeometry;
      if (code === this._scopeCode && geometry === this._scopeGeometry) return;

      this._scopeCode = code;
      this._scopeGeometry = geometry;

      const scope =
        code && geometry
          ? {
              active: true,
              code,
              label:
                ub.selectedDistrictName ||
                ub.selectedProvinceName ||
                ub.selectedDepartmentName ||
                code,
            }
          : { active: false, code: null, label: "" };
      this.updateState((s) => ({ ...s, scope }));

      if (this._lastScopeKey !== code) {
        this._lastScopeKey = code;
        this.clearScopeHighlight();
      }

      // Solo la fuente visible (lo que alimenta la banda y la matriz):
      // recargar no cambia lo que el mapa muestra.
      const tab = this.snapshot().activeTab;
      if (!tab) return;
      const visible =
        tab === "avisos"
          ? this.warningProvider.state().visibleItems
          : this.nowcastingProvider.state().visibleItems;
      const key = [...visible][0];
      if (key) void this.loadFromLayer(tab, key);
    });
  }

  /** Clave de ámbito previa (detecta cambios de ubigeo). */
  private _lastScopeKey: string | null = null;

  /** Código del ubigeo que recorta el cruce (más específico) o null. */
  private _scopeCode: string | null = null;

  /** Geometría del ubigeo activo (Polygon en WebMercator) o null. */
  private _scopeGeometry: Polygon | null | undefined = null;

  /**
   * Recorta anillos de alerta (4326) al polígono del ámbito activo.
   * Con ámbito Nacional devuelve los anillos sin tocar. Un resultado
   * vacío significa "sin zonas de alerta dentro del ámbito".
   * Si la intersección falla, degrada seguro al cruce sin recorte.
   */
  private scopeClipRings(rings: number[][][]): number[][][] {
    const scope = this._scopeGeometry;
    if (!scope) return rings;
    try {
      const scopeGeo = webMercatorUtils.webMercatorToGeographic(scope) as Polygon;
      const sr = new SpatialReference({ wkid: 4326 });
      const out: number[][][] = [];
      for (const r of rings) {
        if (!Array.isArray(r) || r.length < 3) continue;
        const poly = new Polygon({ rings: [r], spatialReference: sr });
        const res = intersectionOperator.execute(poly, scopeGeo) as
          | Polygon
          | null
          | undefined;
        const resRings = (res as Polygon | null)?.rings;
        if (!resRings || resRings.length === 0) continue;
        out.push(...(resRings as number[][][]));
      }
      return out;
    } catch {
      return rings;
    }
  }

  /**
   * Limpia TODO: resaltados del mapa (KPI + ubigeo), detalle expandido y los
   * datos del panel, dejando el cruce EN BLANCO (tipo refresh) para que la
   * próxima fuente recargue desde cero. Lo usan el botón "Limpiar" y el
   * cambio de fuente de alerta (aviso ↔ precipitación).
   */
  clearAll(): void {
    this.clearScopeHighlight();
    this.clearExposedDetail();
    this._lastScopeKey = null;
    // Descarta el cruce anterior y cualquier consulta de detalle en vuelo:
    // sin esto, una respuesta tardía repintaría datos de la fuente saliente.
    this._ringsByLayer.clear();
    this._geometryByLevel.clear();
    this._detailRequest++;
    this.updateState(s => ({
      ...s,
      items: [],
      activeTab: "",
      charts: EMPTY_CHARTS,
      expandedLayerKey: null,
      layerDetails: new Map(),
      computedAt: null,
    }));
  }

  private async loadFromLayer(tab: string, key: string): Promise<void> {
    const isWarning = tab === "avisos";
    const notifier = isWarning
      ? this.warningProvider.notifier
      : this.nowcastingProvider.notifier;
    const features = notifier.getFeaturesForLayer(key);
    if (!features?.length) return;

    this.updateState((s) => ({
      ...s,
      items: [],
      loading: true,
      activeTab: tab,
      expandedLayerKey: null,
      layerDetails: new Map(),
    }));

    const alertLevelMap = new Map<number, number[][][][]>();
    for (const f of features) {
      const existing = alertLevelMap.get(f.level) ?? [];
      existing.push(f.rings);
      alertLevelMap.set(f.level, existing);
    }

    const items: NowcastingKpiState.Item[] = [];
    const ringsByLayer = new Map<number, Map<number, number[][][]>>();
    const geometryByLevel = new Map<number, string>();

    // Verificar si hay un departamento seleccionado en el selector de Ubigeo
    const ubState = this.ref.watch(UbigeoProvider).state();
    const hasDeptScope = Boolean(this._scopeCode && ubState.selectedDepartmentName);

    for (const [alertLevel, polygonRings] of alertLevelMap) {
      const levels: NowcastingKpiState.Level[] = [];
      const mergedRings = polygonRings.flat();
      // Ámbito ubigeo: recortar las zonas de este nivel al polígono
      // seleccionado. Vacío = nivel sin presencia dentro del ámbito → el
      // nivel no existe para este cruce (ni count ni rings que expongan).
      const scopedRings = this.scopeClipRings(mergedRings);
      if (scopedRings.length === 0) continue;

      // Pre-calcular y unificar la geometría de alerta UNA SOLA VEZ para este nivel
      // (evita ejecutar unionOperator 24+ veces redundantes).
      const geometryParam = await this.kpiService.prepareGeometryParam(scopedRings);
      geometryByLevel.set(alertLevel, geometryParam);

      // Enfocarse en las capas activas de elementos expuestos (5, 6, 7) y departamentos (0)
      const tasks = this.kpiService.activeLayers.map(async (layer) => {
        let count: number | null;

        // Optimización de la capa 0 (Departamentos):
        // Si hay un departamento seleccionado y los anillos recortados existen,
        // el departamento en alerta es exactamente 1 (el seleccionado).
        if (layer.id === 0 && hasDeptScope) {
          count = 1;
        } else {
          count = await this.kpiService.queryLayerCount(
            layer.id,
            scopedRings,
            geometryParam,
          );
        }

        levels.push({
          layerId: layer.id,
          name: layer.name,
          count: count ?? 0,
          error: count === null,
        });

        const existing = ringsByLayer.get(layer.id);
        if (existing) {
          const existingRings = existing.get(alertLevel);
          if (existingRings) {
            existingRings.push(...scopedRings);
          } else {
            existing.set(alertLevel, [...scopedRings]);
          }
        } else {
          ringsByLayer.set(layer.id, new Map([[alertLevel, [...scopedRings]]]));
        }
      });

      await Promise.all(tasks);
      items.push({
        level: alertLevel,
        levels: levels.sort((a, b) => a.layerId - b.layerId),
      });
    }

    items.sort((a, b) => a.level - b.level);

    // Capa territorial 0 (Departamentos): deduplicación palpable de unidades
    // (Provincias y Distritos se omiten de la carga general para máxima velocidad).
    const uniqueLevelTotals = await this.computeUniqueTerritorialTotals(
      ringsByLayer,
      geometryByLevel,
      hasDeptScope ? ubState.selectedDepartmentName : null,
    );
    this.chartService.uniqueLevelTotals = uniqueLevelTotals;

    this._ringsByLayer = ringsByLayer;
    this._geometryByLevel = geometryByLevel;

    const charts = this.chartService.computeCharts(items);

    this.updateState((s) => ({
      ...s,
      items,
      loading: false,
      activeTab: tab,
      charts,
      expandedLayerKey: null,
      layerDetails: new Map(),
      computedAt: Date.now(),
    }));
  }

  /**
   * Unidades administrativas únicas por [nivel][layerId] para la capa
   * de Departamentos (layer 0). Si hay un departamento seleccionado en el header,
   * se resuelve instantáneamente sin consultas HTTP repetidas.
   */
  private async computeUniqueTerritorialTotals(
    ringsByLayer: Map<number, Map<number, number[][][]>>,
    geometryByLevel?: Map<number, string>,
    scopedDeptName?: string | null,
  ): Promise<Map<number, Map<number, Set<string>>>> {
    const result = new Map<number, Map<number, Set<string>>>();
    const layerId = 0; // Solo Departamentos

    const ringsByLevel = ringsByLayer.get(layerId);
    if (!ringsByLevel) return result;

    await Promise.all(
      [...ringsByLevel.entries()].map(async ([lvl, polygonRings]) => {
        if (polygonRings.length === 0) return;

        // Si ya hay un departamento seleccionado, el resultado es inmediato
        if (scopedDeptName) {
          if (!result.has(lvl)) result.set(lvl, new Map());
          result.get(lvl)!.set(layerId, new Set([scopedDeptName.trim().toUpperCase()]));
          return;
        }

        const prebuilt = geometryByLevel?.get(lvl);
        const keys = await this.kpiService.queryLayerUniqueKeys(
          layerId,
          polygonRings,
          prebuilt,
        );
        if (!keys) return;

        if (!result.has(lvl)) result.set(lvl, new Map());
        result.get(lvl)!.set(layerId, keys);
      }),
    );
    return result;
  }

  /**
   * Expande/colapsa el detalle de una capa geográfica. La primera
   * expansión lanza la consulta de features (lazy, cacheada en estado).
   */
  toggleLayerDetail(layerId: number, layerName: string): void {
    const key = String(layerId);
    const current = this.snapshot().expandedLayerKey;

    if (current === key) {
      this.updateState((s) => ({ ...s, expandedLayerKey: null }));
      return;
    }

    const existing = this.snapshot().layerDetails.get(key);
    if (existing) {
      this.updateState((s) => ({
        ...s,
        expandedLayerKey: key,
        layerDetails: new Map(s.layerDetails).set(key, {
          ...existing,
          expanded: true,
        }),
      }));
      return;
    }

    const ringsByLevel = this._ringsByLayer.get(layerId);
    if (!ringsByLevel || ringsByLevel.size === 0) return;

    // Drill-down por nivel: niveles presentes (descendente) y arranque
    // en el más severo (N4 > N3 > N2) — la primera impresión es la peor.
    const levels = [...ringsByLevel.keys()].sort((a, b) => b - a);

    const detail: NowcastingKpiState.LayerDetail = {
      layerId,
      layerName,
      level: levels[0] ?? 0,
      levels,
      features: [],
      featuresByLevel: new Map(),
      loading: true,
      expanded: true,
    };

    this.updateState((s) => ({
      ...s,
      expandedLayerKey: key,
      layerDetails: new Map(s.layerDetails).set(key, detail),
    }));

    this.requestLayerFeatures(layerId, key, detail.level);
  }

  /**
   * Cambia el nivel activo del drill-down de la capa (N2/N3/N4). Recarga
   * bajo demanda SOLO las zonas de ese nivel (caché por nivel en el estado).
   */
  selectLayerDetailLevel(layerId: number, level: number): void {
    const key = String(layerId);
    const d = this.snapshot().layerDetails.get(key);
    if (!d || !d.expanded || d.level === level) return;

    const cached = d.featuresByLevel.get(level);
    if (cached) {
      this.updateState((s) => {
        const updated = new Map(s.layerDetails);
        updated.set(key, {
          ...d,
          level,
          features: cached,
          loading: false,
          error: false,
        });
        return { ...s, layerDetails: updated };
      });
      return;
    }

    this.updateState((s) => {
      const updated = new Map(s.layerDetails);
      updated.set(key, { ...d, level, features: [], loading: true, error: false });
      return { ...s, layerDetails: updated };
    });

    this.requestLayerFeatures(layerId, key, level);
  }

  /** Niveles de alerta presentes para una capa del cruce (descendente). */
  layerLevels(layerId: number): number[] {
    const byLevel = this._ringsByLayer.get(layerId);
    if (!byLevel) return [];
    return [...byLevel.keys()].sort((a, b) => b - a);
  }

  /**
   * Reintenta la consulta de detalle de una capa que falló (p. ej. por
   * geometría demasiado grande). Mantiene el detalle expandido mientras
   * vuelve a consultar (mismo nivel activo).
   */
  retryLayerDetail(layerId: number, layerName: string): void {
    const key = String(layerId);
    const ringsByLevel = this._ringsByLayer.get(layerId);
    if (!ringsByLevel || ringsByLevel.size === 0) return;

    const current = this.snapshot().layerDetails.get(key);
    const levels = [...ringsByLevel.keys()].sort((a, b) => b - a);
    const level = current?.level ?? levels[0] ?? 0;

    this.updateState((s) => {
      const updated = new Map(s.layerDetails);
      updated.set(key, {
        layerId,
        layerName,
        level,
        levels,
        features: [],
        featuresByLevel: new Map(current?.featuresByLevel ?? []),
        loading: true,
        expanded: true,
        error: false,
      });
      return { ...s, expandedLayerKey: key, layerDetails: updated };
    });

    this.requestLayerFeatures(layerId, key, level);
  }

  /**
   * Lanza la consulta de features del detalle y aplica la respuesta solo
   * si sigue vigente (token): evita que una respuesta tardía de una
   * capa anterior pise el detalle recién solicitado.
   */
  private requestLayerFeatures(layerId: number, key: string, level: number): void {
    const layer = this.kpiService.layers.find((l) => l.id === layerId);
    const outFields = layer?.outFields ?? [];

    // Consulta por nivel: los anillos de UN nivel → zonas de ese nivel,
    // sin mezclar niveles en la lista (drill-down N2/N3/N4).
    const rings = this._ringsByLayer.get(layerId)?.get(level);
    if (!rings || rings.length === 0) return;

    // Reutilizar la geometría disuelta pre-calculada del nivel (evita re-ejecutar unionRings).
    const prebuiltGeometry = this._geometryByLevel.get(level);

    const token = ++this._detailRequest;

    this.updateState((s) => {
      const updated = new Map(s.layerDetails);
      const d = updated.get(key);
      if (d) updated.set(key, { ...d, loading: true, error: false });
      return { ...s, layerDetails: updated };
    });

    this.kpiService
      .queryLayerFeatures(layerId, rings, outFields, prebuiltGeometry)
      .then(({ features, error }) => {
        if (this._disposed || token !== this._detailRequest) return;
        this.updateState((s) => {
          const updated = new Map(s.layerDetails);
          const d = updated.get(key);
          if (d) {
            const featuresByLevel = new Map(d.featuresByLevel).set(level, features);
            updated.set(key, {
              ...d,
              features,
              featuresByLevel,
              loading: false,
              error,
            });
          }
          return { ...s, layerDetails: updated };
        });
      });
  }

  /**
   * Features (nombre + geometría) de una capa territorial cruzada con
   * las zonas de alerta activas — alimenta el "Ver detalle" de la banda
   * ejecutiva (departamentos/distritos afectados, clic → zoom).
   *
   * Con `level` filtra a UN nivel (anillos de ese nivel) → drill-down
   * N2/N3/N4; sin `level` se cruzan todos los niveles (contexto total).
   */
  /**
   * Token monotónico de la última consulta del "Ver detalle" de la banda
   * ejecutiva: descarta respuestas tardías de un tab/cruce anterior —
   * sin esto, la promesa vieja puede resolver DESPUÉS de la invalidación
   * y repoblar el caché con datos obsoletos.
   */
  private _affectedRequest = 0;

  async queryAffectedFeatures(
    layerId: number,
    level?: number,
  ): Promise<NowcastingKpiState.LayerFeature[] | null> {
    // Cancelación lógica: una consulta nueva deja obsoleta a la anterior.
    const token = ++this._affectedRequest;
    const stale = (): boolean => token !== this._affectedRequest;

    const layer = this.kpiService.layers.find((l) => l.id === layerId);
    if (!layer) return [];

    const ringsByLevel = this._ringsByLayer.get(layerId);
    if (!ringsByLevel) return [];

    const allRings: number[][][] = [];
    if (level != null) {
      const rings = ringsByLevel.get(level);
      if (rings) allRings.push(...rings);
    } else {
      for (const rings of ringsByLevel.values()) allRings.push(...rings);
    }
    if (allRings.length === 0) return [];

    // Optimización: si se consulta el detalle de Departamentos (0) y ya hay un departamento
    // seleccionado en el header, devolverlo directamente sin roundtrip de red.
    if (layerId === 0 && this._scopeCode && this._scopeGeometry) {
      const ub = this.ref.watch(UbigeoProvider).state();
      const name = ub.selectedDepartmentName || this._scopeCode;
      return [
        {
          // El detalle se identifica por nombre; el código INEI del servicio
          // de límites no lo consume ninguna vista, así que no se sintetiza.
          attributes: { NOMBDEP: name },
          geometry: this._scopeGeometry,
        },
      ];
    }

    // outFields dedicados: la capa Distritos trae nativamente el nombre
    // de su departamento y provincia — el "Ver detalle" los aprovecha
    // para ubicar cada distrito ("PISAC — CUSCO / CUSCO").
    const outFieldsByLayer: Record<number, string[]> = {
      0: ['NOMBDEP'],
      1: ['NOMBPROV', 'NOMBDEP'],
      2: ['NOMBDIST', 'NOMBDEP', 'NOMBPROV'],
    };

    const { features } = await this.kpiService.queryLayerFeatures(
      layerId,
      allRings,
      outFieldsByLayer[layerId] ?? layer.outFields,
      undefined,
      '1=1',
      layerId === 0 ? 50 : 500,
    );
    // Respuesta tardía: el usuario ya recargó el KPI u otra consulta
    // — entregar null (no []) para que el llamador NO pise lo nuevo.
    if (stale()) return null;

    // Dedupe por unidad administrativa: un departamento tocado por
    // varios polígonos de alerta aparece N veces en la respuesta; el
    // decisor debe ver cada unidad una sola vez (cifra palpable).
    const seen = new Set<string>();
    const unique: NowcastingKpiState.LayerFeature[] = [];
    for (const f of features) {
      const key = territorialKey(layerId, f.attributes ?? {});
      const dedupeKey = key ?? `__geom:${JSON.stringify(f.geometry)}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      unique.push(f);
    }
    return unique;
  }

  /**
   * Navega el mapa hasta la extensión de una zona concreta (p. ej. un
   * distrito específico o punto crítico) y la RESALTA persistentemente en el
   * mapa (acción "¿dónde?" con feedback visual que no desaparece).
   */
  focusGeometry(geometry: unknown): void {
    if (!geometry) return;
    try {
      if ((geometry as any).x != null && (geometry as any).y != null) {
        const point = Point.fromJSON(geometry as any);
        const merc = webMercatorUtils.geographicToWebMercator(point) as Point;
        this.drawPointHighlight(merc);
        this.mapProvider.notifier.goTo({ target: merc, zoom: 15 });
        return;
      }
      const polygon = Polygon.fromJSON(geometry as any);
      const merc = webMercatorUtils.geographicToWebMercator(polygon) as Polygon;
      const extent = merc.extent;
      if (!extent) return;
      this.drawScopeHighlight(merc);
      this.mapProvider.notifier.goTo(extent.expand(1.25));
    } catch {
      return;
    }
  }

  /**
   * Dibuja el marcador resaltado para una geometría de punto (puntos críticos).
   */
  private drawPointHighlight(point: Point): void {
    const { map } = this.mapProvider.state();
    if (!map) return;

    let layer = map.findLayerById(KPI_HIGHLIGHT_LAYER_ID) as GraphicsLayer;
    if (!layer) {
      layer = new GraphicsLayer({
        id: KPI_HIGHLIGHT_LAYER_ID,
        listMode: 'hide',
      });
      map.add(layer);
    }
    layer.removeAll();
    layer.add(
      new Graphic({
        geometry: point,
        symbol: new SimpleMarkerSymbol({
          color: new Color([255, 152, 0, 0.9]),
          size: 14,
          outline: new SimpleLineSymbol({
            color: new Color([255, 255, 255, 1]),
            width: 2,
          }),
        }),
      }),
    );
  }

  /**
   * Dibuja/actualiza el resaltado persistente de la unidad enfocada
   * (relleno suave + contorno) sobre una GraphicsLayer dedicada.
   */
  private drawScopeHighlight(polygon: Polygon): void {
    const { map } = this.mapProvider.state();
    if (!map) return;

    let layer = map.findLayerById(KPI_HIGHLIGHT_LAYER_ID) as GraphicsLayer;
    if (!layer) {
      layer = new GraphicsLayer({
        id: KPI_HIGHLIGHT_LAYER_ID,
        listMode: 'hide',
      });
      map.add(layer);
    }
    layer.removeAll();
    layer.add(
      new Graphic({
        geometry: polygon,
        symbol: new SimpleFillSymbol({
          // Mismo cian del ubigeo, solo contorno 1.5 (sin relleno): el foco
          // del KPI no compite visualmente con el boundary de la selección.
          color: null as any,
          outline: new SimpleLineSymbol({
            color: new Color([0, 200, 255, 1]),
            width: 1.5,
            style: 'solid',
          }),
        }),
      }),
    );
  }

  /** Limpia el resaltado del KPI (cambio de ámbito del ubigeo). */
  clearScopeHighlight(): void {
    const { map } = this.mapProvider.state();
    if (!map) return;
    const layer = map.findLayerById(KPI_HIGHLIGHT_LAYER_ID) as GraphicsLayer;
    if (layer) layer.removeAll();
  }

  /**
   * Dibuja las geometrías del "Ver detalle" de un elemento expuesto
   * (parcelas, productores, puntos críticos) sobre una GraphicsLayer
   * dedicada. Convierte cada geometría 4326 → WebMercator (el mapa vive
   * en 3857); con `fit` navega al extent combinado para que el decisor
   * vea DÓNDE están los elementos, no solo cuántos son.
   */
  showExposedDetail(
    features: NowcastingKpiState.LayerFeature[],
    color: string,
    fit = false,
  ): void {
    const { map } = this.mapProvider.state();
    if (!map) return;

    let layer = map.findLayerById(KPI_EXPOSED_LAYER_ID) as GraphicsLayer;
    if (!layer) {
      layer = new GraphicsLayer({ id: KPI_EXPOSED_LAYER_ID, listMode: 'hide' });
      map.add(layer);
    }
    layer.removeAll();

    const accent = new Color(color);
    const fill = new Color(color);
    fill.a = 0.22; // relieve suave: la superficie se lee sin tapar el mapa
    const outline = new SimpleLineSymbol({ color: accent, width: 1.1 });
    const markerOutline = new SimpleLineSymbol({
      color: new Color([255, 255, 255, 0.95]),
      width: 1,
    });

    let extent: Extent | null = null;
    for (const f of features) {
      const geo = f.geometry as
        | { x?: number; y?: number; rings?: number[][][]; spatialReference?: unknown }
        | null
        | undefined;
      if (!geo) continue;
      try {
        const isPoint = geo.x != null && geo.y != null;
        const graphic = isPoint
          ? new Graphic({
              geometry: webMercatorUtils.geographicToWebMercator(
                Point.fromJSON(geo as any),
              ) as Point,
              symbol: new SimpleMarkerSymbol({
                color: accent,
                size: 9,
                outline: markerOutline,
              }),
            })
          : new Graphic({
              geometry: webMercatorUtils.geographicToWebMercator(
                Polygon.fromJSON(geo as any),
              ) as Polygon,
              symbol: new SimpleFillSymbol({ color: fill, outline }),
            });
        layer.add(graphic);
        const ext = (graphic.geometry as any).extent as Extent | undefined;
        if (ext) extent = extent ? extent.union(ext) : ext.clone();
      } catch {
        continue; // geometría inválida: no rompe el resto del cruce
      }
    }

    if (fit && extent) {
      this.mapProvider.notifier.goTo(extent.expand(1.35));
    }
  }

  /** Retira del mapa las geometrías del "Ver detalle" agro. */
  clearExposedDetail(): void {
    const { map } = this.mapProvider.state();
    if (!map) return;
    const layer = map.findLayerById(KPI_EXPOSED_LAYER_ID) as GraphicsLayer;
    if (layer) layer.removeAll();
  }
}
